// 3rd party
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import * as debounce from 'lodash.debounce';

// Bernie
import { AnalyticsStore, ContextStore } from 'bernie-core';

// Shared UI
import { LocalizedComponentProps, LocalizedText, withLocalization } from '@shared-ui/localization-context';

// UI Toolkit
import { UitkSecondaryButton } from 'uitk-react-button';
import { UitkIcon } from 'uitk-react-icons';
import { UitkInformativeInput, UitkTextArea } from 'uitk-react-input';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLink } from 'uitk-react-link';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkParagraph } from 'uitk-react-text';
import { MoreInformationDialog, MoreInformationDialogTriggerButton } from 'components/shared/more-information';

// Models
import { Program, Product } from 'models';

// Stores
import { UserAuthStore } from 'stores';

// Utils
import { getProgramAnalyticsMicroMessage, getProductAnalyticsMicroMessage } from '../../util/analytics';

interface SearchProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  userAuth?: UserAuthStore;
  program?: Program;
  product?: Product;
  userProgram?: any;
}

interface SearchState {
  pubref: string;
  bufferedPubref: string;
  errors?: {
    pubref?: string;
  };
}

@withLocalization
@inject('context', 'userAuth', 'analytics')
@observer
class Search extends React.Component<SearchProps, SearchState> {
  private readonly specialChars = ['>', '<', '&', "'", '"'];

  public constructor(props) {
    super(props);

    this.handlePubrefChange = this.handlePubrefChange.bind(this);

    this.state = {
      pubref: '',
      bufferedPubref: '',
      errors: {
        pubref: '',
      },
    };
  }

  private readonly frameRef = React.createRef<HTMLIFrameElement>();

  private copyToClipboardLabel = this.props.l10n.formatText('product.search.code.actions.copy_to_clipboard');
  private copiedToClipboardLabel = this.props.l10n.formatText('product.search.code.actions.copied_to_clipboard');

  public pasteToClipboard(copyText) {
    navigator.clipboard.writeText(copyText);

    const copyButton = document.getElementById('clipboardCopyBtn');
    copyButton.textContent = this.copiedToClipboardLabel;
    setTimeout(() => {
      copyButton.textContent = this.copyToClipboardLabel;
    }, 1500);
  }

  private analyticsTrackEvents = (rfrr: string) => {
    const { analytics, program, product, userProgram } = this.props;

    if (typeof analytics?.trackEvent === 'function') {
      const microMessages = [
        getProgramAnalyticsMicroMessage(program, userProgram.programStatus),
        getProductAnalyticsMicroMessage(product),
      ];
      const filterAnalyticsTrackEvent = {
        'affhub.product_config': () => {
          analytics.trackEvent(rfrr, 'AffHub Product Config Presented', null, null, true, microMessages);
        },
        'affhub_copy_code.selected': () => {
          analytics.trackEvent(rfrr, 'AffHub Copy Code Selected', null, null, true, microMessages);
        },
        'affhub_implementation_guide.selected': () => {
          analytics.trackEvent(rfrr, 'AffHub Implementation Guide Selected', null, null, true, microMessages);
        },
      };

      filterAnalyticsTrackEvent[rfrr]();
    }
  };

  private renderDocumentationLink() {
    const trackImplementationGuideLink = {
      id: 'affhub_implementation_guide.selected',
      callback: () => {
        this.analyticsTrackEvents('affhub_implementation_guide.selected');
      },
    };

    return (
      <UitkSpacing margin={{ blockend: 'six' }}>
        <UitkParagraph size={1}>
          <UitkLink inline={true} analytics={trackImplementationGuideLink}>
            <a href={this.props.product.guideLink} target="_blank">
              <LocalizedText message="product.search.code.implementation_guide" />
              <UitkIcon name="open_in_new" />
            </a>
          </UitkLink>
        </UitkParagraph>
      </UitkSpacing>
    );
  }

  private setBufferedPubref = debounce(function setBufferedPubref(bufferedPubref: string) {
    this.setState({ bufferedPubref });
  }, 250);

  private handlePubrefChange(event) {
    const { l10n } = this.props;
    const {
      value,
      validity: { valid },
    } = event.target;

    this.setState({
      pubref: value,
      errors: {
        pubref: !valid && l10n.formatText('product.form.error.special_chars_invalid', this.specialChars),
      },
    });

    this.setBufferedPubref(value);
  }

  private getUserCamRef = (placeholderCamRef: string = '') => {
    return this.props.userProgram.programReference || placeholderCamRef;
  };

  private getIframeSrc = ({ placeholderCamRef }: { placeholderCamRef?: string } = {}): string => {
    return `${this.props.product.demoLink}&camref=${this.getUserCamRef(placeholderCamRef)}&pubref=`;
  };

  private renderCodeMarkup() {
    const { l10n, program } = this.props;

    const codeMarkup =
      '<div' +
      ' class="eg-widget"' +
      ' data-widget="search"' +
      ` data-program="${program.name}"` +
      ` data-camref="${this.getUserCamRef(
        l10n.formatText('product.search.code.insert_ref_here', ['CAMREF']).toUpperCase(),
      )}"` +
      ` data-pubref="${this.state.bufferedPubref}"` +
      '></div>\n' +
      '<script class="eg-widgets-script" src="https://affiliates.expediagroup.com/widgets/assets/eg-widgets.js"></script>';

    return (
      <UitkTextArea
        id="searchWidgetCode"
        label={l10n.formatText('product.search.code.form.markup.label')}
        value={codeMarkup}
        hasVisibleLabel={false}
      />
    );
  }

  private renderPreviewFrame() {
    const { program } = this.props;

    return (
      <>
        <link
          className="eg-widgets-style"
          rel="stylesheet"
          href="https://affiliates.expediagroup.com/widgets/assets/eg-widgets.css"
        />
        <div
          className="eg-widget eg-search-widget"
          data-widget="search"
          data-program={program.name}
          data-camref={this.getUserCamRef()}
          data-pubref=""
        >
          <iframe ref={this.frameRef} className="eg-widget-frame eg-search-widget-frame" src={this.getIframeSrc()} />
        </div>
        {/*<script className="eg-widgets-script" src="https://affiliates.expediagroup.com/widgets/assets/eg-widgets.js" />*/}
      </>
    );
  }

  private handleMessage = (event: MessageEvent) => {
    if (event?.data.type === 'eg-widget/resize') {
      const { current: frame } = this.frameRef;
      const { payload } = event.data;

      frame.style.height = payload.frame.style.height;
    }
  };

  public componentDidMount() {
    this.analyticsTrackEvents('affhub.product_config');

    window.addEventListener('message', this.handleMessage);
  }

  public componentWillUnmount() {
    window.removeEventListener('message', this.handleMessage);
  }

  public render() {
    const { l10n } = this.props;

    return (
      <main className="container">
        <UitkLayoutGrid columns={{ large: 2 }} space="twelve">
          <UitkLayoutGridItem>
            <div>
              <UitkLayoutGrid space="six">
                <UitkLayoutGridItem>
                  <div>
                    <fieldset>
                      <UitkSpacing margin={{ blockend: 'four' }}>
                        <legend>
                          <UitkHeading tag="h4" size={4}>
                            <LocalizedText message="product.form.tracking-information.pz.heading.partnerize_tracking_information" />
                          </UitkHeading>
                        </legend>
                      </UitkSpacing>

                      <UitkLayoutGrid>
                        <UitkLayoutGridItem>
                          <div>
                            <UitkInformativeInput
                              id="tracking-pubref"
                              name="pubref"
                              label={l10n.formatText('product.form.tracking-information.pz.label.pubref_optional')}
                              informativeTrigger={<MoreInformationDialogTriggerButton id="pz-pubref" />}
                              a11yInformativeTrigger={l10n.formatText(
                                'more-information.dialog.trigger.more_information',
                              )}
                              value={this.state.pubref}
                              onChange={this.handlePubrefChange}
                              pattern={`[^${this.specialChars.join('')}]*`}
                              invalid={this.state.errors?.pubref}
                            />
                            <MoreInformationDialog
                              id="pz-pubref"
                              title={l10n.formatText('more-information.dialog.pz-pubref.title.what_pubref')}
                              description={l10n.formatText(
                                'more-information.dialog.pz-pubref.description.pubref_optional_reference_include_tracking_link_for_your_reporting_purposes_pubref_tracks_clicks_through_bookings',
                              )}
                            />
                          </div>
                        </UitkLayoutGridItem>
                      </UitkLayoutGrid>
                    </fieldset>
                  </div>
                </UitkLayoutGridItem>

                <UitkLayoutGridItem>
                  <div>
                    <UitkHeading tag="h4" size={4}>
                      <LocalizedText message="product.search.code.title" />
                    </UitkHeading>

                    <UitkParagraph size={1}>
                      <LocalizedText message="product.search.code.instructions" />
                    </UitkParagraph>
                    {this.renderDocumentationLink()}

                    {this.renderCodeMarkup()}
                    <UitkParagraph size={1}>
                      <UitkSecondaryButton
                        id="clipboardCopyBtn"
                        onClick={() => {
                          const textArea = document.getElementById('searchWidgetCode') as HTMLTextAreaElement;
                          textArea.focus();
                          this.pasteToClipboard(textArea.value);
                          this.analyticsTrackEvents('affhub_copy_code.selected');
                        }}
                      >
                        {this.copyToClipboardLabel}
                      </UitkSecondaryButton>
                    </UitkParagraph>
                  </div>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            </div>
          </UitkLayoutGridItem>

          <UitkLayoutGridItem>
            <div>
              <UitkHeading tag="h4" size={4}>
                <LocalizedText message="product.search.preview.title" />
              </UitkHeading>

              <UitkSpacing margin={{ blockend: 'six' }}>
                <UitkParagraph size={1}>
                  <LocalizedText message="product.search.preview.information" />
                </UitkParagraph>
              </UitkSpacing>

              {this.renderPreviewFrame()}
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </main>
    );
  }
}

export { Search };
